import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, Pencil, PlusCircle, Trash2 } from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader } from '@/components/loader';
import { useToast } from '@/components/ui/use-toast';
import { getById, updateOrganization } from '@/api/organization.api.private';
import { useOrganizationContext } from '../../organization.context';
import {
  EXCLUDE,
  ITEM_PATH_LABELS,
  formatOutcome,
  hasTargetConflict,
  normalizeRule,
  readRules,
  readStoredRules,
  BUCKETS,
} from './constants';
import { combinedMatchMultiplier, hasWarning, ruleAdvisories } from './rule-advisories';
import { useRankingRuleOptions } from './use-ranking-rule-options';
import RuleEditor from './rule-editor';

const serialize = rules => JSON.stringify(rules);

const OutcomeCell = ({ rule }) => (
  <div className="flex gap-1">
    {BUCKETS.map(bucket =>
      rule[bucket] === EXCLUDE ? (
        <Badge key={bucket} variant="destructive" className="font-normal">
          {formatOutcome(rule[bucket])}
        </Badge>
      ) : (
        <span key={bucket} className="text-muted-foreground w-12 text-sm">
          {formatOutcome(rule[bucket])}
        </span>
      ),
    )}
  </div>
);

export default function RulesSheet({ open, onOpenChange }) {
  const { organization, setOrganization } = useOrganizationContext();
  const { toast } = useToast();
  const options = useRankingRuleOptions();

  const [rules, setRules] = useState(() => readRules(organization));
  const [snapshot, setSnapshot] = useState(() => serialize(readRules(organization)));
  // Frozen at mount: a stored rule set both exclusive peers and had to be repaired.
  const [repaired] = useState(() => readStoredRules(organization).some(hasTargetConflict));
  const [view, setView] = useState('list');
  const [editingIndex, setEditingIndex] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [discarding, setDiscarding] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirty = serialize(rules) !== snapshot;

  const close = useCallback(() => {
    setDiscarding(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const requestClose = nextOpen => {
    if (nextOpen) return;
    if (dirty) {
      setDiscarding(true);
      return;
    }
    close();
  };

  const advisoriesByIndex = useMemo(() => {
    if (options.loading) return [];
    return rules.map(rule => ruleAdvisories(rule, options));
  }, [rules, options]);

  const duplicateKeys = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    rules.forEach(rule => {
      if (seen.has(rule.key)) dupes.add(rule.key);
      seen.add(rule.key);
    });
    return dupes;
  }, [rules]);

  const stacking = useMemo(() => combinedMatchMultiplier(rules), [rules]);

  // Normalized on the way in: `dirty` compares JSON strings, and the editor's key
  // order differs from the stored one — an untouched rule would read as changed.
  const saveRule = apiRule => {
    const rule = normalizeRule(apiRule);
    setRules(prev => (editingIndex === null ? [...prev, rule] : prev.with(editingIndex, rule)));
    setEditingIndex(null);
    setView('list');
  };

  const toggleEnabled = (index, enabled) =>
    setRules(prev => prev.map((rule, i) => (i === index ? { ...rule, enabled } : rule)));

  const onSave = async () => {
    setSaving(true);

    // The whole array is replaced on write, so a concurrent edit would be
    // silently clobbered. Re-read and bail if it moved under us.
    const { data: fresh, error: readError } = await getById(organization.id);
    if (readError) {
      setSaving(false);
      toast({
        title: 'Could not verify the current rules',
        description: readError?.message ?? 'Failed to re-read the organization before saving.',
        variant: 'destructive',
      });
      return;
    }
    if (serialize(readRules(fresh)) !== snapshot) {
      setSaving(false);
      setOrganization(prev => ({ ...prev, ...fresh }));
      toast({
        title: 'Rules changed since you opened this',
        description:
          'Someone else saved a different set of rules. Close and reopen to start from theirs — saving now would discard their changes.',
        variant: 'destructive',
      });
      return;
    }

    const { data, error } = await updateOrganization({
      id: organization.id,
      data: { internalConfigurations: { search: { rules } } },
    });
    setSaving(false);

    if (error) {
      toast({
        title: 'Failed to save ranking rules',
        description: data?.message ?? 'An error occurred while saving the rules',
        variant: 'destructive',
      });
      return;
    }

    const savedRules = readRules(data);
    setOrganization(prev => ({ ...prev, ...data }));
    setRules(savedRules);
    setSnapshot(serialize(savedRules));
    toast({ title: 'Ranking rules updated' });
  };

  const renderList = () => (
    <>
      <div className="flex-1 space-y-4 overflow-y-auto p-6 pt-0">
        {repaired && (
          <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            Some stored rules were in a shape the API rejects — a rule cannot carry both a profile
            field and a fixed list. They are shown here reading from the profile field. Review them
            before saving; until you save, the stored version is unchanged.
          </p>
        )}
        {rules.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">
            No ranking rules yet. Results are ordered purely by text relevance.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">On</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Field</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Match / Unassigned / Other</TableHead>
                <TableHead>Workspaces</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule, index) => {
                const advisories = advisoriesByIndex[index] ?? [];
                const warn = hasWarning(advisories);

                if (deletingIndex === index) {
                  return (
                    <TableRow key={index} className="bg-destructive/5">
                      <TableCell colSpan={7}>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm">
                            Delete <strong>{rule.key}</strong>? It stops affecting search as soon as
                            you save.
                          </span>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeletingIndex(null)}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setRules(prev => prev.filter((_, i) => i !== index));
                                setDeletingIndex(null);
                              }}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <TableRow key={index}>
                    <TableCell>
                      <Switch
                        checked={rule.enabled !== false}
                        onCheckedChange={checked => toggleEnabled(index, checked)}
                        aria-label={`Enable ${rule.key}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {rule.key}
                        {duplicateKeys.has(rule.key) && (
                          <Badge variant="outline" className="border-amber-300 text-amber-700">
                            duplicate name
                          </Badge>
                        )}
                        {warn && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                {advisories
                                  .filter(a => a.level === 'warning')
                                  .map(a => (
                                    <p key={a.message}>{a.message}</p>
                                  ))}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{ITEM_PATH_LABELS[rule.itemPath] ?? rule.itemPath}</TableCell>
                    <TableCell className="text-sm">
                      {rule.userPath ? (
                        <span>the searcher&apos;s {options.userPathLabel(rule.userPath)}</span>
                      ) : (
                        <span className="text-muted-foreground">
                          {(rule.values ?? [])
                            .map(value => options.valueLabel(rule.itemPath, value))
                            .join(', ')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <OutcomeCell rule={rule} />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {rule.workspaceIds?.length
                        ? rule.workspaceIds.map(options.workspaceLabel).join(', ')
                        : 'Whole organization'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Edit ${rule.key}`}
                          onClick={() => {
                            setEditingIndex(index);
                            setView('edit');
                          }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Delete ${rule.key}`}
                          onClick={() => setDeletingIndex(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="space-y-3 border-t p-4">
        {stacking && (
          <p className="text-muted-foreground text-xs">
            {stacking.count} enabled rules can apply to the same result, giving a combined boost of
            ×{Number(stacking.product.toFixed(2))}
            {stacking.isClamped && ` — clamped to ×${stacking.clamped}`}.
          </p>
        )}
        {discarding ? (
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm">Discard your unsaved changes?</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setDiscarding(false)}>
                Keep editing
              </Button>
              <Button size="sm" variant="destructive" onClick={close}>
                Discard
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground text-sm">
              {dirty ? 'Unsaved changes' : 'Everything saved'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => requestClose(false)}>
                Close
              </Button>
              <Button onClick={onSave} loading={saving} disabled={!dirty || saving}>
                Save rules
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderBody = () => {
    if (options.loading) return <Loader />;
    if (options.error) {
      return (
        <div className="text-destructive p-6 text-sm">
          Failed to load this organization&apos;s labels, workspaces or profile fields. Rules can
          only be edited against the real vocabularies, so the editor is unavailable — try reloading
          the page.
        </div>
      );
    }
    if (view === 'edit') {
      return (
        <RuleEditor
          rule={editingIndex === null ? null : rules[editingIndex]}
          existingKeys={rules
            .filter((_, index) => index !== editingIndex)
            .map(rule => rule.key?.toUpperCase())}
          options={options}
          onSave={saveRule}
          onCancel={() => {
            setEditingIndex(null);
            setView('list');
          }}
        />
      );
    }
    return renderList();
  };

  return (
    <Sheet open={open} onOpenChange={requestClose}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-5xl">
        {/* pr-14 keeps the header clear of the Sheet's own close button. */}
        <SheetHeader className="flex-row items-start justify-between space-y-0 p-6 pr-14">
          <div className="space-y-1">
            <SheetTitle>Search ranking rules</SheetTitle>
            <SheetDescription>
              Reorder or hide search results for this organization, on top of text relevance.
              Changes take effect on the next search — no reindex.
            </SheetDescription>
          </div>
          {view === 'list' && !options.loading && !options.error && (
            <Button
              onClick={() => {
                setEditingIndex(null);
                setView('edit');
              }}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add rule
            </Button>
          )}
        </SheetHeader>
        {renderBody()}
      </SheetContent>
    </Sheet>
  );
}
